import { useState } from 'react'
import { Template, ProgrammaticTemplate, DOOHTemplate } from '../../types'
import { motion } from 'framer-motion'
import { Download, Eye, Copy, Star, Check, Plus, Info } from 'lucide-react'

interface TemplatesRecommenderProps {
  highImpactTemplates: Template[]
  programmaticTemplates: ProgrammaticTemplate[]
  doohTemplates: DOOHTemplate[]
  onCreateAdUnits?: (selectedTemplateIds: string[], selectedSizes: Map<string, Set<string>>) => void
}

// Template Card Component for High Impact Templates
const HighImpactTemplateCard = ({ 
  template, 
  index, 
  isSelected, 
  onSelect,
  selectedSizes,
  onSizeSelect
}: { 
  template: Template
  index: number
  isSelected: boolean
  onSelect: (id: string) => void
  selectedSizes?: Set<string>
  onSizeSelect?: (templateId: string, size: string) => void
}) => {
  const templateSelectedSizes = selectedSizes || new Set<string>()
  
  return (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.1 }}
    className={`bg-white rounded-lg shadow-sm border-2 overflow-hidden hover:shadow-lg transition-all duration-200 ${
      isSelected 
        ? templateSelectedSizes.size > 0 
          ? 'border-primary-500 bg-primary-50/30' 
          : 'border-amber-500 bg-amber-50/30'
        : 'border-gray-200'
    }`}
  >
    {/* Header Section with Title, Badges, Rating, and Checkbox */}
    <div className="p-4 border-b border-gray-200 bg-gray-50/50">
      <div className="flex items-center justify-between gap-3 mb-2">
        {/* Selection Checkbox */}
        <button
          onClick={() => onSelect(template.id)}
          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-200 flex-shrink-0 ${
            isSelected
              ? 'bg-primary-600 border-primary-600'
              : 'bg-white border-gray-400 hover:border-primary-500 hover:bg-gray-50'
          }`}
        >
          {isSelected && <Check className="h-4 w-4 text-white stroke-[3]" />}
        </button>
        
        {/* Template Title */}
        <h3 className="text-lg font-bold text-gray-900 flex-1">{template.name}</h3>
        
        {/* Relevance Score Badge */}
        {template.relevanceScore && (
          <span className="px-3 py-1 bg-green-500 text-white rounded-full text-xs font-medium">
            {template.relevanceScore}% Match
          </span>
        )}
        
        {/* Complexity Badge */}
        <span className={`px-3 py-1 text-xs rounded-full font-medium ${
          template.complexityLevel === 'High' ? 'bg-red-100 text-red-800' :
          template.complexityLevel === 'Medium' ? 'bg-green-100 text-green-800' :
          'bg-yellow-100 text-yellow-800'
        }`}>
          {template.complexityLevel?.toLowerCase() || 'medium'}
        </span>
        
        {/* Star Rating */}
        <div className="flex items-center space-x-1">
          <Star className="h-4 w-4 text-yellow-400 fill-current" />
          <span className="text-sm font-medium text-gray-900">4.8</span>
        </div>
      </div>
      
      {/* Selected Sizes Indicator */}
      {isSelected && (
        <div className="mt-2 pt-2 border-t border-gray-200">
          {templateSelectedSizes.size > 0 ? (
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-600">Selected Sizes:</span>
              <div className="flex flex-wrap gap-1">
                {Array.from(templateSelectedSizes).map((size, idx) => (
                  <span
                    key={idx}
                    className="px-2 py-0.5 text-xs bg-primary-600 text-white rounded-full font-medium flex items-center gap-1"
                  >
                    <Check className="h-3 w-3" />
                    {size}
                  </span>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-amber-600 flex items-center gap-1">
                <span className="w-4 h-4 rounded-full bg-amber-500 text-white flex items-center justify-center text-[10px]">!</span>
                Please select at least one size
              </span>
            </div>
          )}
        </div>
      )}
    </div>

    {/* Template Preview */}
    <div className="aspect-video bg-gray-100 flex items-center justify-center relative">
      {template.previewUrl ? (
        <div className="w-full h-full">
          <iframe
            src={template.previewUrl}
            className="w-full h-full border-0"
            title={`${template.name} Preview`}
          />
        </div>
      ) : template.imageUrl ? (
        <img
          src={template.imageUrl}
          alt={template.name}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="text-center text-gray-500">
          <Eye className="h-12 w-12 mx-auto mb-2" />
          <p className="text-sm">Template Preview</p>
        </div>
      )}
    </div>
    
    {/* Content Section */}
    <div className="p-4">
      <p className="text-gray-600 mb-4">{template.description}</p>
      
      {/* Performance Metrics */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="bg-gray-50 p-3 rounded-lg">
          <div className="text-sm text-gray-500">Expected CTR</div>
          <div className="font-semibold text-gray-900">{template.benchmarkCtr}</div>
        </div>
        <div className="bg-gray-50 p-3 rounded-lg">
          <div className="text-sm text-gray-500">Production Time</div>
          <div className="font-semibold text-gray-900">{template.productionTime}</div>
        </div>
      </div>

      {/* Available Sizes - Selectable */}
      {template.availableSizes && template.availableSizes.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-900">Available Sizes</h4>
            {isSelected && templateSelectedSizes.size > 0 && (
              <span className="text-xs text-gray-500">
                {templateSelectedSizes.size} selected
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {template.availableSizes.map((size, idx) => {
              const isSizeSelected = templateSelectedSizes.has(size)
              const isDisabled = !isSelected
              return (
                <button
                  key={idx}
                  type="button"
                  disabled={isDisabled}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (!isDisabled) {
                      onSizeSelect?.(template.id, size)
                    }
                  }}
                  className={`px-3 py-1.5 text-xs rounded-lg border-2 transition-all duration-200 ${
                    isDisabled
                      ? 'bg-gray-50 text-gray-500 border-gray-300 cursor-not-allowed opacity-75'
                      : isSizeSelected
                        ? 'bg-primary-600 border-primary-600 text-white font-medium cursor-pointer'
                        : 'bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200 hover:border-blue-300 cursor-pointer'
                  }`}
                  title={isDisabled ? 'Please select template first' : ''}
                >
                  {isSizeSelected && <Check className="h-3 w-3 inline-block mr-1" />}
                  {size}
                </button>
              )
            })}
          </div>
          {!isSelected && (
            <p className="text-xs text-gray-500 mt-2 italic">
              Select template to choose sizes
            </p>
          )}
        </div>
      )}

      {/* Best Use Cases */}
      {template.bestUseCases && template.bestUseCases.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Best Use Cases</h4>
          <div className="flex flex-wrap gap-1">
            {template.bestUseCases.slice(0, 3).map((useCase, idx) => (
              <span
                key={idx}
                className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full"
              >
                {useCase}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Why This Template */}
      {template.reasons && template.reasons.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-gray-900 mb-2">Why This Template?</h4>
          <ul className="text-sm text-gray-600 space-y-1">
            {template.reasons.slice(0, 3).map((reason, idx) => (
              <li key={idx} className="flex items-start">
                <Check className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span>{reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex space-x-2">
        <button className="flex-1 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors duration-200 flex items-center justify-center">
          <Eye className="h-4 w-4 mr-1" />
          Preview
        </button>
        <button className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors duration-200">
          <Download className="h-4 w-4" />
        </button>
        <button className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors duration-200">
          <Copy className="h-4 w-4" />
        </button>
      </div>
    </div>
  </motion.div>
  )
}

// Template Card Component for Programmatic Templates
const ProgrammaticTemplateCard = ({ 
  template, 
  index, 
  isSelected, 
  onSelect,
  selectedSizes,
  onSizeSelect
}: { 
  template: ProgrammaticTemplate
  index: number
  isSelected: boolean
  onSelect: (id: string) => void
  selectedSizes?: Set<string>
  onSizeSelect?: (templateId: string, size: string) => void
}) => (
  <HighImpactTemplateCard 
    template={template as any} 
    index={index} 
    isSelected={isSelected}
    onSelect={onSelect}
    selectedSizes={selectedSizes}
    onSizeSelect={onSizeSelect}
  />
)

// Template Card Component for DOOH Templates
const DOOHTemplateCard = ({ 
  template, 
  index, 
  isSelected, 
  onSelect,
  selectedSizes,
  onSizeSelect
}: { 
  template: DOOHTemplate
  index: number
  isSelected: boolean
  onSelect: (id: string) => void
  selectedSizes?: Set<string>
  onSizeSelect?: (templateId: string, size: string) => void
}) => (
  <HighImpactTemplateCard 
    template={template as any} 
    index={index} 
    isSelected={isSelected}
    onSelect={onSelect}
    selectedSizes={selectedSizes}
    onSizeSelect={onSizeSelect}
  />
)

export function TemplatesRecommender({ 
  highImpactTemplates, 
  programmaticTemplates, 
  doohTemplates,
  onCreateAdUnits
}: TemplatesRecommenderProps) {
  const [selectedTemplates, setSelectedTemplates] = useState<Set<string>>(new Set())
  // Track selected sizes per template: templateId -> Set of selected sizes
  const [selectedSizes, setSelectedSizes] = useState<Map<string, Set<string>>>(new Map())

  const allTemplates = [
    ...highImpactTemplates,
    ...programmaticTemplates,
    ...doohTemplates
  ]

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplates(prev => {
      const newSet = new Set(prev)
      if (newSet.has(templateId)) {
        newSet.delete(templateId)
        // Clear sizes when deselecting template
        setSelectedSizes(prev => {
          const newMap = new Map(prev)
          newMap.delete(templateId)
          return newMap
        })
      } else {
        newSet.add(templateId)
      }
      return newSet
    })
  }

  // Validation: Check if all selected templates have at least one size selected
  const isValidSelection = () => {
    const templateIds = Array.from(selectedTemplates)
    for (const templateId of templateIds) {
      const template = allTemplates.find(t => t.id === templateId)
      const templateSizes = selectedSizes.get(templateId)
      
      // If template has available sizes, must have at least one selected
      if (template?.availableSizes && template.availableSizes.length > 0) {
        if (!templateSizes || templateSizes.size === 0) {
          return false
        }
      }
    }
    return true
  }

  const handleCreateAdUnits = () => {
    const selectedIds = Array.from(selectedTemplates)
    
    // Validate that all templates have sizes selected
    if (!isValidSelection()) {
      // Show validation error - templates without sizes
      return
    }
    
    if (selectedIds.length > 0 && onCreateAdUnits) {
      onCreateAdUnits(selectedIds, selectedSizes)
      // Clear selection after creating
      setSelectedTemplates(new Set())
      setSelectedSizes(new Map())
    }
  }
  
  const hasInvalidTemplates = !isValidSelection()

  const isAllSelected = selectedTemplates.size > 0 && selectedTemplates.size === allTemplates.length
  const isPartialSelected = selectedTemplates.size > 0 && selectedTemplates.size < allTemplates.length

  const handleSelectAll = () => {
    if (isAllSelected) {
      // Deselect all
      setSelectedTemplates(new Set())
      setSelectedSizes(new Map())
    } else {
      // Select all
      const allIds = allTemplates.map(t => t.id)
      setSelectedTemplates(new Set(allIds))
    }
  }

  const handleSizeSelect = (templateId: string, size: string) => {
    setSelectedSizes(prev => {
      const newMap = new Map(prev)
      // Create a new Set from existing sizes or create a new empty Set
      const existingSizes = newMap.get(templateId)
      const templateSizes = existingSizes ? new Set(existingSizes) : new Set<string>()
      
      // Toggle the size selection
      if (templateSizes.has(size)) {
        templateSizes.delete(size)
      } else {
        templateSizes.add(size)
      }
      
      // Update the map with the new Set
      if (templateSizes.size > 0) {
        newMap.set(templateId, templateSizes)
      } else {
        newMap.delete(templateId)
      }
      
      return newMap
    })
  }

  return (
    <div className="space-y-4">
      {/* Header Section - Compact Dark Theme */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gray-900 rounded-lg shadow-sm p-3 lg:p-4"
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-3">
          {/* Left Side - Title and Description */}
          <div className="flex-1 min-w-0">
            <h2 className="text-base lg:text-lg font-bold text-white mb-0.5">Templates Recommender</h2>
            <p className="text-xs text-gray-300">AI-powered recommendations tailored for your campaign</p>
          </div>

          {/* Right Side - Actions */}
          <div className="flex flex-col items-end gap-2 flex-shrink-0 w-full sm:w-auto">
            <div className="flex items-center gap-2">
              {/* Select All Button */}
              <button
                onClick={handleSelectAll}
                className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  isAllSelected
                    ? 'bg-gray-700 text-white hover:bg-gray-600'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700'
                }`}
              >
                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                  isAllSelected
                    ? 'bg-white border-white'
                    : 'border-gray-400'
                }`}>
                  {(isAllSelected || isPartialSelected) && <Check className="h-3 w-3 text-gray-900" />}
                </div>
                <span className="whitespace-nowrap">
                  {isAllSelected ? 'Deselect All' : 'Select All'}
                  {isPartialSelected && ` (${selectedTemplates.size}/${allTemplates.length})`}
                </span>
              </button>

              {/* Create Ad Units Button */}
              {selectedTemplates.size > 0 ? (
                <button
                  onClick={handleCreateAdUnits}
                  disabled={hasInvalidTemplates}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                    hasInvalidTemplates
                      ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                      : 'bg-orange-500 text-white hover:bg-orange-600'
                  }`}
                >
                  <Plus className="h-4 w-4" />
                  <span>Create Ad Units</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                    hasInvalidTemplates ? 'bg-gray-600 text-gray-300' : 'bg-white/20 text-white'
                  }`}>
                    {selectedTemplates.size}
                  </span>
                </button>
              ) : (
                <div className="px-4 py-2 bg-gray-800 rounded-full text-xs text-gray-400">
                  No templates selected
                </div>
              )}
            </div>

            {/* Warning message if invalid templates - Below action buttons */}
            {hasInvalidTemplates && selectedTemplates.size > 0 && (
              <div className="flex items-center gap-2 text-xs text-amber-400">
                <span className="w-4 h-4 rounded-full bg-amber-500 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">!</span>
                <span>Please select sizes for all templates</span>
              </div>
            )}
          </div>
        </div>

        {/* Instructions - Compact */}
        <div className="mt-2 pt-2 border-t border-gray-700">
          <div className="flex items-start gap-2">
            <Info className="h-3 w-3 text-gray-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs text-gray-400">
                <span className="font-semibold text-gray-300">How to use: </span>
                Select templates, choose sizes, then click "Create Ad Units"
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* High Impact Templates */}
      {highImpactTemplates && highImpactTemplates.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow-sm border p-4"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <span className="mr-2">🔥</span>
              High Impact Templates
            </h3>
            <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">
              {highImpactTemplates.length} templates
            </span>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {highImpactTemplates.map((template, index) => (
              <HighImpactTemplateCard 
                key={template.id} 
                template={template} 
                index={index}
                isSelected={selectedTemplates.has(template.id)}
                onSelect={handleTemplateSelect}
                selectedSizes={selectedSizes.get(template.id)}
                onSizeSelect={handleSizeSelect}
              />
            ))}
          </div>
        </motion.div>
      )}

      {/* Programmatic Templates */}
      {programmaticTemplates && programmaticTemplates.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg shadow-sm border p-4"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <span className="mr-2">🤖</span>
              Programmatic Templates
            </h3>
            <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
              {programmaticTemplates.length} templates
            </span>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {programmaticTemplates.map((template, index) => (
              <ProgrammaticTemplateCard 
                key={template.id} 
                template={template} 
                index={index}
                isSelected={selectedTemplates.has(template.id)}
                onSelect={handleTemplateSelect}
                selectedSizes={selectedSizes.get(template.id)}
                onSizeSelect={handleSizeSelect}
              />
            ))}
          </div>
        </motion.div>
      )}

      {/* DOOH Templates */}
      {doohTemplates && doohTemplates.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-lg shadow-sm border p-4"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <span className="mr-2">🏢</span>
              DOOH Templates
            </h3>
            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
              {doohTemplates.length} templates
            </span>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {doohTemplates.map((template, index) => (
              <DOOHTemplateCard 
                key={template.id} 
                template={template} 
                index={index}
                isSelected={selectedTemplates.has(template.id)}
                onSelect={handleTemplateSelect}
                selectedSizes={selectedSizes.get(template.id)}
                onSizeSelect={handleSizeSelect}
              />
            ))}
          </div>
        </motion.div>
      )}
    </div>
  )
}

